/**
 * StrategyStateService — single source of truth for device strategy state.
 *
 * Manages the selected catalog and assembly of device parts.
 * Broadcasts PubSub events on every change.
 */

import { PubSubService } from '@/visualization/core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '@/visualization/core/types';
import type {
    FieldSelections,
    IAssemblyEntry,
    ICatalog,
    IntroductionSide,
    IStrategyStateDTO,
    PartType,
    Side,
} from '@/workflows/vascular/common/catalog';
import { resolveReference } from '@/workflows/vascular/common/catalog';

// ============================================================================
// Service
// ============================================================================

export class StrategyStateService extends PubSubService {
    static readonly SERVICE_NAME = 'StrategyStateService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: StrategyStateService.SERVICE_NAME,
        factory: (context: ServiceContext) => new StrategyStateService(context),
    };

    readonly EVENTS = {
        STRATEGY_INITIALIZED: 'strategy:initialized',
        CATALOG_SELECTED: 'strategy:catalog:selected',
        INTRODUCTION_SIDE_CHANGED: 'strategy:introduction-side:changed',
        PART_ADDED: 'strategy:part:added',
        PART_REMOVED: 'strategy:part:removed',
        FIELD_CHANGED: 'strategy:field:changed',
        ASSEMBLY_CLEARED: 'strategy:assembly:cleared',
        STALENESS_UPDATED: 'strategy:staleness:updated',
    } as const;

    /** Events that represent user mutations (trigger persistence). */
    get MUTATION_EVENTS(): string[] {
        return [
            this.EVENTS.CATALOG_SELECTED,
            this.EVENTS.INTRODUCTION_SIDE_CHANGED,
            this.EVENTS.PART_ADDED,
            this.EVENTS.PART_REMOVED,
            this.EVENTS.FIELD_CHANGED,
            this.EVENTS.ASSEMBLY_CLEARED,
        ];
    }

    /** All events (trigger snapshot rebuild in React). */
    get ALL_EVENTS(): string[] {
        return [...this.MUTATION_EVENTS, this.EVENTS.STRATEGY_INITIALIZED, this.EVENTS.STALENESS_UPDATED];
    }

    // ── State ──
    private _initialized = false;
    private _selectedCatalogId: string | null = null;
    private _introductionSide: IntroductionSide = 'right';
    private _assembly: IAssemblyEntry[] = [];
    private _nextInstanceId = 1;

    constructor(_context: ServiceContext) {
        super();
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    /**
     * Initialize the strategy service.
     * Called after workflow data is loaded. Signals that persistence can restore state.
     */
    initialize(): void {
        this._initialized = true;
        this.broadcast(this.EVENTS.STRATEGY_INITIALIZED);
    }

    isInitialized(): boolean {
        return this._initialized;
    }

    // ========================================================================
    // Catalog Selection
    // ========================================================================

    selectCatalog(catalogId: string | null): void {
        if (this._selectedCatalogId === catalogId) return;
        this._selectedCatalogId = catalogId;
        this.broadcast(this.EVENTS.CATALOG_SELECTED, { catalogId });
    }

    getSelectedCatalogId(): string | null {
        return this._selectedCatalogId;
    }

    // ========================================================================
    // Introduction Side
    // ========================================================================

    setIntroductionSide(side: IntroductionSide): void {
        if (this._introductionSide === side) return;
        this._introductionSide = side;
        this.markStale();
        this.broadcast(this.EVENTS.INTRODUCTION_SIDE_CHANGED, { side });
    }

    getIntroductionSide(): IntroductionSide {
        return this._introductionSide;
    }

    // ========================================================================
    // Assembly Mutations
    // ========================================================================

    /**
     * Add a part to the assembly.
     *
     * If `exclusiveGroupPartIds` is provided, any existing assembly entry whose
     * partId is in that set **and** matches the same side will be replaced
     * (exclusive group radio-toggle behavior).
     *
     * @returns The new instance ID.
     */
    addPart(
        catalogId: string,
        partId: string,
        partType: PartType,
        side: 'left' | 'right' | 'none',
        partLabel: string,
        fieldSelections?: FieldSelections,
        resolvedRef?: string | null,
        exclusiveGroupPartIds?: ReadonlySet<string>
    ): string {
        // Remove conflicting entries from the same exclusive group + side + catalog
        if (exclusiveGroupPartIds && exclusiveGroupPartIds.size > 0) {
            this._assembly = this._assembly.filter(
                (e) => !(e.catalogId === catalogId && exclusiveGroupPartIds.has(e.partId) && e.side === side)
            );
        }

        const instanceId = String(this._nextInstanceId++);
        const entry: IAssemblyEntry = {
            instanceId,
            catalogId,
            partId,
            partType,
            partLabel,
            side,
            fieldSelections: fieldSelections ?? {},
            resolvedRef: resolvedRef ?? null,
            stale: false,
            count: 1,
        };
        this._assembly = [...this._assembly, entry];
        this.broadcast(this.EVENTS.PART_ADDED, { instanceId });
        return instanceId;
    }

    /**
     * Add a part from a catalog, resolving its reference and exclusive group automatically.
     * This is the primary API for UI components adding parts.
     */
    addPartFromCatalog(
        catalog: ICatalog,
        partId: string,
        side: Side,
        fieldSelections: FieldSelections
    ): string {
        const part = catalog.parts.find((p) => p.id === partId);
        if (!part) return '';

        const ref = resolveReference(part, fieldSelections);

        let exclusiveGroupPartIds: ReadonlySet<string> | undefined;
        if (part.exclusiveGroup) {
            exclusiveGroupPartIds = new Set(
                catalog.parts.filter((p) => p.exclusiveGroup === part.exclusiveGroup).map((p) => p.id)
            );
        }

        return this.addPart(
            catalog.id,
            partId,
            part.type,
            side,
            part.label,
            fieldSelections,
            ref,
            exclusiveGroupPartIds
        );
    }

    removePart(instanceId: string): void {
        const prev = this._assembly;
        this._assembly = prev.filter((e) => e.instanceId !== instanceId);
        if (this._assembly.length !== prev.length) {
            this.broadcast(this.EVENTS.PART_REMOVED, { instanceId });
        }
    }

    setFieldValue(instanceId: string, column: string, value: string | number | null): void {
        this._assembly = this._assembly.map((entry) => {
            if (entry.instanceId !== instanceId) return entry;
            return {
                ...entry,
                fieldSelections: { ...entry.fieldSelections, [column]: value },
                stale: false, // Selecting a value clears staleness
            };
        });
        this.broadcast(this.EVENTS.FIELD_CHANGED, { instanceId, column, value });
    }

    /**
     * Update the resolved reference for an assembly entry.
     * Called by the UI after re-computing reference resolution.
     */
    setResolvedRef(instanceId: string, ref: string | null): void {
        let changed = false;
        this._assembly = this._assembly.map((entry) => {
            if (entry.instanceId !== instanceId) return entry;
            if (entry.resolvedRef === ref) return entry;
            changed = true;
            return { ...entry, resolvedRef: ref };
        });
        if (changed) {
            this.broadcast(this.EVENTS.FIELD_CHANGED, { instanceId, column: '__ref', value: ref });
        }
    }

    clearAssembly(catalogId?: string): void {
        if (this._assembly.length === 0) return;
        if (catalogId) {
            const prev = this._assembly;
            this._assembly = prev.filter((e) => e.catalogId !== catalogId);
            if (this._assembly.length === prev.length) return;
        } else {
            this._assembly = [];
            this._nextInstanceId = 1;
        }
        this.broadcast(this.EVENTS.ASSEMBLY_CLEARED);
    }

    /**
     * Mark all assembly entries as stale (measurements changed).
     * Called reactively when sizing measurements are updated.
     */
    markStale(): void {
        if (this._assembly.length === 0) return;

        const allStale = this._assembly.every((e) => e.stale);
        if (allStale) return;

        this._assembly = this._assembly.map((entry) => (entry.stale ? entry : { ...entry, stale: true }));
        this.broadcast(this.EVENTS.STALENESS_UPDATED);
    }

    /**
     * Clear staleness flag on a specific entry.
     */
    clearStale(instanceId: string): void {
        let changed = false;
        this._assembly = this._assembly.map((entry) => {
            if (entry.instanceId !== instanceId || !entry.stale) return entry;
            changed = true;
            return { ...entry, stale: false };
        });
        if (changed) {
            this.broadcast(this.EVENTS.STALENESS_UPDATED);
        }
    }

    // ========================================================================
    // Getters
    // ========================================================================

    getAssembly(): readonly IAssemblyEntry[] {
        return this._assembly;
    }

    getEntry(instanceId: string): IAssemblyEntry | undefined {
        return this._assembly.find((e) => e.instanceId === instanceId);
    }

    // ========================================================================
    // Serialization
    // ========================================================================

    serializeState(): IStrategyStateDTO {
        return {
            selectedCatalogId: this._selectedCatalogId,
            introductionSide: this._introductionSide,
            assembly: this._assembly.map((e) => ({ ...e, stale: false })),
        };
    }

    loadState(dto: IStrategyStateDTO): void {
        this._selectedCatalogId = dto.selectedCatalogId;
        this._introductionSide = dto.introductionSide ?? 'right';
        this._assembly = (dto.assembly ?? []).map((e) => ({
            ...e,
            catalogId: e.catalogId || dto.selectedCatalogId || '',
            partType: e.partType ?? 'standard',
            count: e.count ?? 1,
        }));
        const numericIds = this._assembly.map((e) => Number(e.instanceId)).filter((n) => Number.isFinite(n));
        this._nextInstanceId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
        // Signal consumers that state has been restored
        this.broadcast(this.EVENTS.STRATEGY_INITIALIZED);
    }

    // ========================================================================
    // Reset
    // ========================================================================

    override reset(): void {
        this._initialized = false;
        this._selectedCatalogId = null;
        this._introductionSide = 'right';
        this._assembly = [];
        this._nextInstanceId = 1;
        super.reset();
    }
}
